import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { alpha } from '@mui/material/styles';

import { Container, Typography, Box, Grid, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, FormGroup, FormControlLabel, Checkbox, InputAdornment, IconButton, Collapse, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SpeedIcon from '@mui/icons-material/Speed';
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import FunctionsIcon from '@mui/icons-material/Functions';
import TuneIcon from '@mui/icons-material/Tune';
import StarIcon from '@mui/icons-material/Star';
import LanguageIcon from '@mui/icons-material/Language';
import NotesIcon from '@mui/icons-material/Notes';
import ApiIcon from '@mui/icons-material/Api';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CreateIcon from '@mui/icons-material/Create';
import CategoryIcon from '@mui/icons-material/Category';
import ShortTextIcon from '@mui/icons-material/ShortText';
import FindInPageIcon from '@mui/icons-material/FindInPage';
// Additional icons for more descriptive benchmarks
import ScienceIcon from '@mui/icons-material/Science';
import TerminalIcon from '@mui/icons-material/Terminal';
import BugReportIcon from '@mui/icons-material/BugReport';
import WebIcon from '@mui/icons-material/Web';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import DiamondIcon from '@mui/icons-material/Diamond';
import CalculateIcon from '@mui/icons-material/Calculate';
import BuildIcon from '@mui/icons-material/Build';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import PaletteIcon from '@mui/icons-material/Palette';
import { llmModels, normalizeProvider, initialProviderFilters } from './modelData';

// Helper function to reset filters to their default state
export const resetFilters = (setFilters, setSearchTerm) => {
  setFilters({
    openSource: false,
    providers: initialProviderFilters
  });
  setSearchTerm('');
};

// Helper function to get the appropriate icon for a metric
const getMetricIcon = (metricName) => {
  const lowerMetricName = metricName.toLowerCase();
  
  // SWE-bench and Terminal-bench - software engineering benchmarks
  if (lowerMetricName.includes('swe-bench') || lowerMetricName.includes('swe bench')) {
    return <BugReportIcon />;
  } else if (lowerMetricName.includes('terminal-bench') || lowerMetricName.includes('terminal bench')) {
    return <TerminalIcon />;
  }
  // Code-related benchmarks
  else if (lowerMetricName.includes('code') || lowerMetricName.includes('humaneval')) {
    return <CodeIcon />;
  }
  // GPQA Diamond - scientific reasoning
  else if (lowerMetricName.includes('gpqa diamond')) {
    return <DiamondIcon />;
  } else if (lowerMetricName.includes('gpqa')) {
    return <ScienceIcon />;
  }
  // AIME - mathematical reasoning
  else if (lowerMetricName.includes('aime')) {
    return <CalculateIcon />;
  }
  // Math-related benchmarks
  else if (lowerMetricName.includes('math') || lowerMetricName.includes('gsm8k')) {
    return <FunctionsIcon />;
  }
  // Frontend and UI/UX benchmarks
  else if (lowerMetricName.includes('frontend dev') || lowerMetricName.includes('frontend')) {
    return <DeveloperModeIcon />;
  } else if (lowerMetricName.includes('ui/ux') || lowerMetricName.includes('ui') || lowerMetricName.includes('ux')) {
    return <PaletteIcon />;
  }
  // Web-related benchmarks
  else if (lowerMetricName.includes('web')) {
    return <WebIcon />;
  }
  // Speed benchmarks
  else if (lowerMetricName.includes('speed') || lowerMetricName.includes('t/s')) {
    return <SpeedIcon />;
  }
  // Educational benchmarks
  else if (lowerMetricName.includes('mmlu')) {
    return <SchoolIcon />;
  }
  // Tool use and general capabilities
  else if (lowerMetricName.includes('tool use')) {
    return <BuildIcon />;
  }
  // Reasoning benchmarks
  else if (lowerMetricName.includes('reasoning')) {
    return <PsychologyIcon />;
  }
  // Language benchmarks
  else if (lowerMetricName.includes('multilingual') || lowerMetricName.includes('language') || 
            lowerMetricName.includes('cross-lingual') || lowerMetricName.includes('low-resource')) {
    return <LanguageIcon />;
  }
  // Default fallback
  return <VerifiedIcon />;
};

// Updated helper function to get the appropriate icon for a feature
const getFeatureIcon = (featureName) => {
  const lowerFeatureName = featureName.toLowerCase();
  if (lowerFeatureName.includes('text generation') || lowerFeatureName.includes('content creation')) {
    return <CreateIcon />;
  } else if (lowerFeatureName.includes('classification')) {
    return <CategoryIcon />;
  } else if (lowerFeatureName.includes('summarization')) {
    return <ShortTextIcon />;
  } else if (lowerFeatureName.includes('extraction')) {
    return <FindInPageIcon />;
  } else if (lowerFeatureName.includes('multilingual') || lowerFeatureName.includes('language') || lowerFeatureName.includes('translation')) {
    return <LanguageIcon />;
  } else if (lowerFeatureName.includes('context window') || lowerFeatureName.includes('context length') || (lowerFeatureName.includes('tokens') && !lowerFeatureName.includes('output tokens'))) {
    return <NotesIcon />;
  } else if (lowerFeatureName.includes('api') || lowerFeatureName.includes('sdk') || lowerFeatureName.includes('integration')) {
    return <ApiIcon />;
  } else if (lowerFeatureName.includes('json output') || lowerFeatureName.includes('structured data')) {
    return <DataObjectIcon />;
  } else if (lowerFeatureName.includes('fine-tuning') || lowerFeatureName.includes('customization')) {
    return <TuneIcon />;
  } else if (lowerFeatureName.includes('enterprise') || lowerFeatureName.includes('business') || lowerFeatureName.includes('production')) {
    return <BusinessCenterIcon />;
  } else if (lowerFeatureName.includes('reasoning') || lowerFeatureName.includes('logic')) {
    return <PsychologyIcon />;
  } else if (lowerFeatureName.includes('code generation') || lowerFeatureName.includes('coding') || lowerFeatureName.includes('programming')) {
    return <CodeIcon />;
  } else if (lowerFeatureName.includes('vision') || lowerFeatureName.includes('image') || lowerFeatureName.includes('multimodal')) {
    return <VisibilityIcon />;
  } else if (lowerFeatureName.includes('speed') || lowerFeatureName.includes('efficiency') || lowerFeatureName.includes('fast')) {
    return <SpeedIcon />;
  }
  return <StarIcon />; // Default icon for features
};

// Helper function to get the appropriate color for a metric
const getMetricColor = (metricName) => {
  const lowerMetricName = metricName.toLowerCase();
  if (lowerMetricName.includes('code') || lowerMetricName.includes('swe')) {
    return 'rgba(25, 118, 210, 0.1)'; // blue
  } else if (lowerMetricName.includes('gpqa') || lowerMetricName.includes('aime')) {
    return 'rgba(156, 39, 176, 0.1)'; // purple
  } else if (lowerMetricName.includes('speed') || lowerMetricName.includes('t/s')) {
    return 'rgba(76, 175, 80, 0.1)'; // green
  } else if (lowerMetricName.includes('frontend') || lowerMetricName.includes('ui/ux') || lowerMetricName.includes('ui') || lowerMetricName.includes('ux')) {
    return 'rgba(255, 152, 0, 0.1)'; // orange
  } else if (lowerMetricName.includes('terminal-bench') || lowerMetricName.includes('terminal')) {
    return 'rgba(96, 125, 139, 0.1)'; // blue grey
  } else if (lowerMetricName.includes('multilingual') || lowerMetricName.includes('language') || 
             lowerMetricName.includes('cross-lingual') || lowerMetricName.includes('low-resource')) {
    return 'rgba(3, 169, 244, 0.1)'; // light blue
  }
  return 'rgba(158, 158, 158, 0.1)'; // grey
};

const ModelCard = ({ model }) => {
  return (
    <Card 
      component={RouterLink} 
      to={`/models/${model.id}`}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        position: 'relative',
        overflow: 'visible',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          '& .model-card-glow': {
            opacity: 0.8
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, primary.main, secondary.main)',
          borderRadius: '2px 2px 0 0'
        }
      }}
    >
      <Box
        className="model-card-glow"
        sx={{
          position: 'absolute',
          top: -2,
          left: -2,
          right: -2,
          bottom: -2,
          background: 'linear-gradient(45deg, primary.main 0%, secondary.main 100%)',
          borderRadius: 'inherit',
          opacity: 0,
          transition: 'opacity 0.3s ease-in-out',
          zIndex: -1
        }}
      />
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom={false} sx={{ color: 'primary.main', fontWeight: 'bold', mb: 0.5 }}>
              {model.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0, mb: 0 }}>
              {model.provider}
            </Typography>
          </Box>
          {model.isOpenSource && (
            <LockOpenIcon 
              sx={{ 
                color: 'success.main',
                backgroundColor: alpha('#4caf50', 0.1),
                p: 0.5,
                borderRadius: 1
              }} 
            />
          )}
        </Box>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            mb: 2,
            mt: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {model.description}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
          {model.keyMetrics && model.keyMetrics.length > 0 ? (
            model.keyMetrics.slice(0, 3).map((metric, index) => (
              <Chip
                key={`metric-${index}`}
                size="small"
                label={`${metric.name}: ${metric.score}`}
                icon={getMetricIcon(metric.name)}
                sx={{ 
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'text.primary',
                  '& .MuiChip-icon': {
                    color: (theme) => {
                      const lowerMetricName = metric.name.toLowerCase();
                      if (lowerMetricName.includes('swe-bench') || lowerMetricName.includes('swe bench')) return theme.palette.primary.main;
                      if (lowerMetricName.includes('terminal-bench') || lowerMetricName.includes('terminal bench')) return theme.palette.info.main;
                      if (lowerMetricName.includes('code') || lowerMetricName.includes('humaneval')) return theme.palette.primary.main;
                      if (lowerMetricName.includes('gpqa diamond')) return theme.palette.secondary.main;
                      if (lowerMetricName.includes('gpqa') || lowerMetricName.includes('aime')) return theme.palette.secondary.main;
                      if (lowerMetricName.includes('frontend dev') || lowerMetricName.includes('frontend')) return theme.palette.primary.main;
                      if (lowerMetricName.includes('ui/ux') || lowerMetricName.includes('ui') || lowerMetricName.includes('ux')) return theme.palette.secondary.main;
                      if (lowerMetricName.includes('speed') || lowerMetricName.includes('t/s')) return theme.palette.success.main;
                      if (lowerMetricName.includes('mmlu')) return theme.palette.warning.dark;
                      if (lowerMetricName.includes('math') || lowerMetricName.includes('gsm8k')) return theme.palette.error.dark;
                      if (lowerMetricName.includes('tool use')) return theme.palette.info.main;
                      if (lowerMetricName.includes('multilingual') || lowerMetricName.includes('language') || 
                          lowerMetricName.includes('cross-lingual') || lowerMetricName.includes('low-resource')) return theme.palette.info.dark;
                      return theme.palette.info.main;
                    }
                  }
                }}
              />
            ))
          ) : model.features && model.features.length > 0 ? (
            model.features.slice(0, 3).map((feature, index) => (
              <Chip
                key={`feature-${index}`}
                size="small"
                label={feature}
                icon={getFeatureIcon(feature)}
                sx={{ 
                  backgroundColor: (theme) => alpha(theme.palette.info.light, 0.1),
                  color: 'text.primary',
                  '& .MuiChip-icon': {
                    color: (theme) => theme.palette.info.main
                  }
                }}
              />
            ))
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

const Home = ({ filters, setFilters, searchTerm, setSearchTerm }) => {
  const [showFilters, setShowFilters] = useState(false);

  const filteredModels = useMemo(() => {
    return llmModels.filter(model => {
      // Check open source filter
      if (filters.openSource && !model.isOpenSource) {
        return false;
      }
      
      // Check provider filters
      const normalizedProvider = normalizeProvider(model.provider);
      const anyProviderSelected = Object.values(filters.providers).some(v => v);
      if (anyProviderSelected && !filters.providers[normalizedProvider]) {
        return false;
      }
      
      // Check search term
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase();
        const nameMatch = model.name.toLowerCase().includes(term);
        const providerMatch = model.provider.toLowerCase().includes(term);
        const descriptionMatch = model.description && model.description.toLowerCase().includes(term);
        const featuresMatch = model.features && model.features.some(feature => 
          feature.toLowerCase().includes(term)
        );
        
        return nameMatch || providerMatch || descriptionMatch || featuresMatch;
      }
      
      return true;
    });
  }, [filters, searchTerm]);

  // Create JSON-LD schema data for item list
  const jsonLdData = {
    "@context": "https://schema.org/",
    "@type": "ItemList",
    "itemListElement": filteredModels.slice(0, 20).map((model, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": model.name,
        "description": model.description,
        "url": `/models/${model.id}`,
        "brand": {
          "@type": "Brand",
          "name": model.provider
        }
      }
    }))
  };

  // Check if any provider is selected
  const anyProviderSelected = Object.values(filters.providers).some(value => value);
  
  return (
    <>
      <Helmet>
        <title>AI Models Directory - Compare LLMs and Foundation Models</title>
        <meta name="description" content="Explore and compare AI language models from leading providers. Find specifications, benchmarks, and pricing information for GPT, Claude, Gemini, Llama, and more." />
        <meta property="og:title" content="AI Models Directory - Compare LLMs and Foundation Models" />
        <meta property="og:description" content="Explore and compare AI language models from leading providers. Find specifications, benchmarks, and pricing information for GPT, Claude, Gemini, Llama, and more." />
        <meta name="twitter:title" content="AI Models Directory - Compare LLMs and Foundation Models" />
        <meta name="twitter:description" content="Explore and compare AI language models from leading providers. Find specifications, benchmarks, and pricing information for GPT, Claude, Gemini, Llama, and more." />
        <link rel="canonical" href="/" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdData)}
        </script>
      </Helmet>

      <Container maxWidth="lg" component="main" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search models by name, provider, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                  }
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              Filters
            </Button>
          </Box>

          <Collapse in={showFilters}>
            <Paper 
              sx={{ 
                p: 3, 
                mb: 3,
                border: '1px solid rgba(0, 0, 0, 0.15)',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Filter Models
                </Typography>
                <Button size="small" onClick={() => {
                  setFilters({
                    openSource: false,
                    providers: initialProviderFilters
                  });
                  setSearchTerm('');
                }}>
                  Clear All
                </Button>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Model Type
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.openSource}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          openSource: e.target.checked
                        }))}
                          size="small"
                        />
                      }
                      label="Open Source"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Providers
                  </Typography>
                  <FormGroup>
                    {Object.keys(filters.providers).map((provider) => (
                      <FormControlLabel
                        key={provider}
                        control={
                          <Checkbox
                            checked={filters.providers[provider]}
                          onChange={(e) => setFilters(prev => ({
                            ...prev,
                            providers: { ...prev.providers, [provider]: e.target.checked }
                          }))}
                            size="small"
                          />
                        }
                        label={provider}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {filteredModels.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No models match your search criteria
              </Typography>
              <Button 
                variant="text" 
                onClick={() => {
                  setFilters({
                    openSource: false,
                    providers: initialProviderFilters
                  });
                  setSearchTerm('');
                }}
                sx={{ mt: 2 }}
              >
                Clear all filters
              </Button>
            </Box>
          ) : (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {filteredModels.length} models
            </Typography>
          )}
        </Box>

        <Grid container spacing={3}>
          {filteredModels.map((model) => (
            <Grid item xs={12} sm={6} md={4} key={model.id}>
              <ModelCard model={model} />
            </Grid>
          ))}
        </Grid>

        {/* Benchmarks Table */}
        {anyProviderSelected && filteredModels.length > 0 && (
          <Paper sx={{ mt: 4, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Benchmarks & Pricing Comparison
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">GPQA Diamond</TableCell>
                    <TableCell align="right">AIME 2024</TableCell>
                    <TableCell align="right">Code SWE</TableCell>
                    <TableCell align="right">Speed t/s</TableCell>
                    <TableCell align="right">Input Price</TableCell>
                    <TableCell align="right">Output Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredModels.map((model) => (
                    <TableRow key={model.id}>
                      <TableCell>{model.name}</TableCell>
                      <TableCell align="right">
                        {model.keyMetrics?.find(m => m.name === "GPQA Diamond")?.score || "-"}
                      </TableCell>
                      <TableCell align="right">
                        {model.keyMetrics?.find(m => m.name === "AIME 2024")?.score || "-"}
                      </TableCell>
                      <TableCell align="right">
                        {model.keyMetrics?.find(m => m.name === "Code SWE")?.score || "-"}
                      </TableCell>
                      <TableCell align="right">
                        {model.keyMetrics?.find(m => m.name === "Speed t/s")?.score || "-"}
                      </TableCell>
                      <TableCell align="right">
                        {model.inputPrice ? `$${model.inputPrice}` : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {model.outputPrice ? `$${model.outputPrice}` : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default Home; 